import { type ChannelSection, searchChannel } from '../../api/channelApi';
import { type PlaylistDto, fetchPlaylist } from '../../api/playlistApi';
import { type EnqueueMode, enqueueItem } from '../../api/queueApi';
import { closeSearch, fetchSearchPage, startSearch } from '../../api/searchApi';
import type { VideoDto, VideoPage } from '../../api/videoDto';
import { formatDuration, formatUploadDate } from '../../format';
import { isPlaylistUrl } from '../../playlistUrl';
import { parseSearchLimit } from '../../searchLimit';
import downloadFormTemplate from './DownloadForm.html';
import './DownloadForm.scss';

/** Rappels fournis par l'appelant pour réagir aux actions du formulaire. */
export interface DownloadFormCallbacks {
	/** Appelé juste après l'ajout réussi d'un ou plusieurs éléments à la file d'attente. */
	onItemQueued: () => void;
}

/** Ligne de la liste de résultats : une vidéo et son bouton d'ajout. */
interface ResultRow {
	/** Vidéo affichée sur cette ligne. */
	readonly video: VideoDto;
	/** Bouton « Ajouter » de la ligne (désactivé une fois la vidéo ajoutée, ou pendant son ajout). */
	readonly button: HTMLButtonElement;
}

/** Options d'affichage d'une liste de résultats complète. */
interface ShowVideosOptions {
	/** Message de statut affiché au-dessus de la liste (ex. « 12 résultat(s). »). */
	readonly status: string;
	/** Affiche le bouton « Tout ajouter à la file ». */
	readonly allowAddAll: boolean;
}

/** Options d'affichage d'une liste paginée en défilement infini. */
interface ShowPagedOptions {
	/** Première page, déjà chargée. */
	readonly firstPage: VideoPage;
	/** Charge la page qui commence à la position `offset` (le `nextOffset` de la page précédente). */
	readonly loadMore: (offset: number) => Promise<VideoPage>;
	/** Message de statut pour `count` vidéos affichées ; `hasMore` indique qu'il en reste à charger. */
	readonly formatStatus: (count: number, hasMore: boolean) => string;
	/**
	 * Appelé une seule fois quand la liste est remplacée ou vidée alors qu'il
	 * restait des pages à charger (ex. pour libérer la recherche côté
	 * serveur). Jamais appelé si tout tenait dans la première page.
	 */
	readonly onClose: () => void;
}

/** Défilement infini de la liste actuellement affichée. */
interface Pager {
	/** Options fournies par l'appelant. */
	readonly options: ShowPagedOptions;
	/** Dernier élément de la liste : sa visibilité déclenche le chargement de la suite. */
	readonly sentinel: HTMLLIElement;
	/** Observateur de visibilité de {@link Pager.sentinel} dans la zone de défilement. */
	readonly observer: IntersectionObserver;
	/** Position de la prochaine page, ou `null` quand tout est chargé. */
	nextOffset: number | null;
	/** Une page est en cours de chargement. */
	loading: boolean;
}

/** État affiché par l'élément de fin de liste d'un défilement infini. */
type SentinelState =
	| { readonly kind: 'idle' }
	| { readonly kind: 'loading' }
	| { readonly kind: 'end' }
	| { readonly kind: 'error'; readonly message: string; readonly onRetry: () => void };

/** Zone de résultats partagée entre la recherche de chaîne, de playlist et globale. */
interface ResultsView {
	/** Vide la liste et affiche un message d'attente. */
	showLoading: (message: string) => void;
	/** Vide et masque toute la zone de résultats. */
	clear: () => void;
	/** Affiche les vidéos fournies d'un coup, avec leur message de statut. */
	showVideos: (videos: readonly VideoDto[], options: ShowVideosOptions) => void;
	/** Affiche une première page, puis charge la suite au fil du défilement. */
	showPaged: (options: ShowPagedOptions) => void;
}

const ADD_ALL_LABEL = 'Tout ajouter à la file';

/** Nombre de résultats demandés par page pour une recherche sans limite. */
const SEARCH_PAGE_SIZE = 20;

/**
 * Récupère un élément du gabarit, en échouant franchement s'il manque : le
 * gabarit est statique, son absence est une erreur de programmation, pas un
 * cas à ignorer silencieusement.
 *
 * @param root - Racine du formulaire monté.
 * @param selector - Sélecteur CSS de l'élément attendu.
 * @returns L'élément trouvé.
 * @throws {Error} Si le gabarit ne contient pas l'élément.
 */
function requireElement<T extends HTMLElement>(root: HTMLElement, selector: string): T {
	const element = root.querySelector<T>(selector);
	if (!element) {
		throw new Error(`Élément introuvable dans le gabarit : ${selector}`);
	}
	return element;
}

/**
 * Affiche ou efface un message dans un élément de statut/erreur.
 *
 * @param el - Élément cible, ou `null` s'il n'a pas été trouvé dans le gabarit.
 * @param message - Message à afficher, ou `null` pour masquer l'élément.
 */
function setMessage(el: HTMLElement | null, message: string | null): void {
	if (!el) {
		return;
	}
	el.hidden = message === null;
	el.textContent = message ?? '';
}

/**
 * @param error - Erreur capturée (de type inconnu).
 * @param fallback - Message à utiliser si `error` n'est pas une `Error`.
 * @returns Le message à présenter à l'utilisateur.
 */
function describeError(error: unknown, fallback: string): string {
	return error instanceof Error ? error.message : fallback;
}

/**
 * Lit le mode de téléchargement et ses options depuis les contrôles
 * actuellement sélectionnés dans le formulaire.
 *
 * @param root - Racine du formulaire monté.
 * @returns Le mode à utiliser pour le prochain élément ajouté.
 */
function readCurrentMode(root: HTMLElement): EnqueueMode {
	const selected =
		root.querySelector<HTMLInputElement>('input[name="mode"]:checked')?.value ?? 'video';

	if (selected === 'audio') {
		const audioFormat =
			(root.querySelector<HTMLSelectElement>('[data-field="audioFormat"]')?.value as
				| 'mp3'
				| 'm4a'
				| 'opus'
				| undefined) ?? 'mp3';
		return { mode: 'audio', audioFormat };
	}
	if (selected === 'thumbnail') {
		return { mode: 'thumbnail' };
	}
	return {
		mode: 'video',
		subtitles: root.querySelector<HTMLInputElement>('[data-field="subtitles"]')?.checked ?? false,
		embedThumbnail:
			root.querySelector<HTMLInputElement>('[data-field="embedThumbnail"]')?.checked ?? false,
	};
}

/**
 * Affiche/masque les panneaux d'options quand le mode sélectionné change.
 *
 * @param root - Racine du formulaire monté.
 */
function wireModeToggle(root: HTMLElement): void {
	const fieldset = root.querySelector<HTMLFieldSetElement>('.download-form__mode');
	const optionPanels = root.querySelectorAll<HTMLElement>('[data-mode-options]');

	fieldset?.addEventListener('change', () => {
		const selected = root.querySelector<HTMLInputElement>('input[name="mode"]:checked')?.value;
		for (const panel of optionPanels) {
			panel.hidden = panel.dataset.modeOptions !== selected;
		}
	});
}

/**
 * Ajoute la vidéo d'une ligne à la file d'attente et marque son bouton
 * « Ajouté ✓ ». Le bouton est désactivé dès le début (pas de double ajout
 * pendant la requête) et réactivé si l'ajout échoue.
 *
 * @param row - Ligne de résultat à ajouter.
 * @param mode - Mode de téléchargement à appliquer.
 * @throws {Error} Si l'ajout échoue.
 */
async function enqueueResult(row: ResultRow, mode: EnqueueMode): Promise<void> {
	row.button.disabled = true;
	try {
		await enqueueItem(row.video.url, mode, row.video.title, row.video.thumbnailUrl);
		row.button.textContent = 'Ajouté ✓';
	} catch (error) {
		row.button.disabled = false;
		throw error;
	}
}

/**
 * Construit l'élément de liste d'une vidéo (miniature, titre, durée/date et
 * bouton d'ajout), sans y brancher de comportement.
 *
 * @param video - Vidéo à afficher.
 * @returns L'élément `<li>` et son bouton « Ajouter ».
 */
function renderResultItem(video: VideoDto): {
	element: HTMLLIElement;
	button: HTMLButtonElement;
} {
	const element = document.createElement('li');
	element.className = 'download-form__result';

	if (video.thumbnailUrl) {
		const img = document.createElement('img');
		img.src = video.thumbnailUrl;
		img.alt = '';
		// Une playlist ou une chaîne peut compter des centaines de vidéos :
		// sans chargement différé, chaque miniature serait demandée d'un coup.
		img.loading = 'lazy';
		img.className = 'download-form__result-thumb';
		element.append(img);
	}

	const info = document.createElement('div');
	info.className = 'download-form__result-info';

	const title = document.createElement('p');
	title.className = 'download-form__result-title';
	title.textContent = video.title;
	info.append(title);

	const meta = document.createElement('p');
	meta.className = 'download-form__result-meta';
	meta.textContent = [formatDuration(video.durationSeconds), formatUploadDate(video.uploadDate)]
		.filter((part) => part.length > 0)
		.join(' · ');
	info.append(meta);

	element.append(info);

	const button = document.createElement('button');
	button.type = 'button';
	button.textContent = 'Ajouter';
	element.append(button);

	return { element, button };
}

/**
 * Affiche l'état d'un élément de fin de liste (chargement en cours, erreur
 * avec « Réessayer », fin des résultats, ou rien).
 *
 * @param sentinel - Élément de fin de liste.
 * @param state - État à afficher.
 */
function renderSentinel(sentinel: HTMLElement, state: SentinelState): void {
	sentinel.replaceChildren();

	switch (state.kind) {
		case 'idle':
			return;
		case 'loading':
			sentinel.textContent = 'Chargement…';
			return;
		case 'end':
			sentinel.textContent = 'Fin des résultats.';
			return;
		case 'error': {
			const message = document.createElement('span');
			message.textContent = state.message;
			const retry = document.createElement('button');
			retry.type = 'button';
			retry.className = 'secondary';
			retry.textContent = 'Réessayer';
			retry.addEventListener('click', state.onRetry);
			sentinel.append(message, retry);
			return;
		}
	}
}

/**
 * Câble la zone de résultats partagée : liste de vidéos avec un bouton
 * « Ajouter » par ligne, un bouton « Tout ajouter à la file » quand la liste
 * le permet (playlist), et un chargement de la suite au fil du défilement
 * quand la liste est paginée (recherche globale sans limite).
 *
 * @param root - Racine du formulaire monté.
 * @param callbacks - Rappels du formulaire.
 * @returns La vue permettant d'y afficher des résultats.
 */
function createResultsView(root: HTMLElement, callbacks: DownloadFormCallbacks): ResultsView {
	const header = requireElement<HTMLElement>(root, '[data-role="results-header"]');
	const statusEl = requireElement<HTMLElement>(root, '[data-role="results-status"]');
	const addAllButton = requireElement<HTMLButtonElement>(root, '[data-role="add-all"]');
	const errorEl = requireElement<HTMLElement>(root, '[data-role="results-error"]');
	const list = requireElement<HTMLElement>(root, '[data-role="results"]');

	let rows: ResultRow[] = [];
	let pager: Pager | null = null;
	// Incrémenté à chaque remplacement de la liste : une opération encore en
	// cours sur l'ancienne liste (ajout groupé, chargement d'une page) ne doit
	// plus toucher à l'affichage (bouton, message, lignes) de la nouvelle.
	let generation = 0;

	const logUnexpected = (error: unknown): void => {
		console.error('Erreur inattendue dans la liste de résultats', error);
	};

	const closePager = (): void => {
		if (!pager) {
			return;
		}
		const closing = pager;
		pager = null;
		closing.observer.disconnect();
		closing.options.onClose();
	};

	const clear = (): void => {
		generation += 1;
		closePager();
		rows = [];
		list.replaceChildren();
		addAllButton.hidden = true;
		setMessage(errorEl, null);
		header.hidden = true;
	};

	const showLoading = (message: string): void => {
		clear();
		setMessage(statusEl, message);
		header.hidden = false;
	};

	const addOne = (row: ResultRow): void => {
		const startedIn = generation;
		setMessage(errorEl, null);
		enqueueResult(row, readCurrentMode(root))
			.then(() => {
				callbacks.onItemQueued();
			})
			.catch((error: unknown) => {
				if (startedIn === generation) {
					setMessage(errorEl, describeError(error, "Échec de l'ajout."));
				}
			});
	};

	const addAll = async (): Promise<void> => {
		const startedIn = generation;
		const pending = rows.filter((row) => !row.button.disabled);
		if (pending.length === 0) {
			return;
		}

		// Mode lu une seule fois : changer de mode en cours d'ajout ne doit pas
		// mélanger les modes au sein d'un même lot.
		const mode = readCurrentMode(root);
		addAllButton.disabled = true;
		setMessage(errorEl, null);

		let processed = 0;
		let failures = 0;
		let firstFailure: string | null = null;
		// Ajouts séquentiels : l'ordre de la playlist est conservé dans la file.
		for (const row of pending) {
			if (row.button.disabled) {
				// Ajoutée entre-temps depuis son propre bouton.
				continue;
			}
			processed += 1;
			if (startedIn === generation) {
				addAllButton.textContent = `Ajout… ${processed}/${pending.length}`;
			}
			try {
				await enqueueResult(row, mode);
			} catch (error) {
				failures += 1;
				firstFailure ??= describeError(error, "Échec de l'ajout.");
			}
		}

		callbacks.onItemQueued();

		if (startedIn !== generation) {
			return;
		}
		if (failures === 0) {
			addAllButton.textContent = 'Tout ajouté ✓';
			return;
		}
		addAllButton.disabled = false;
		addAllButton.textContent = ADD_ALL_LABEL;
		setMessage(
			errorEl,
			`${failures} vidéo(s) n'ont pas pu être ajoutée(s) : ${firstFailure ?? 'erreur inconnue'}`,
		);
	};

	addAllButton.addEventListener('click', () => {
		addAll().catch((error: unknown) => {
			console.error("Erreur inattendue lors de l'ajout groupé", error);
		});
	});

	/**
	 * Ajoute des lignes en fin de liste (avant l'élément de fin s'il y en a un).
	 */
	const appendRows = (videos: readonly VideoDto[]): void => {
		const fragment = document.createDocumentFragment();
		for (const video of videos) {
			const { element, button } = renderResultItem(video);
			const row: ResultRow = { video, button };
			button.addEventListener('click', () => {
				addOne(row);
			});
			rows.push(row);
			fragment.append(element);
		}

		if (pager) {
			pager.sentinel.before(fragment);
		} else {
			list.append(fragment);
		}
	};

	const showVideos = (videos: readonly VideoDto[], options: ShowVideosOptions): void => {
		clear();
		setMessage(statusEl, options.status);
		header.hidden = false;
		addAllButton.hidden = !options.allowAddAll;
		addAllButton.disabled = false;
		addAllButton.textContent = ADD_ALL_LABEL;
		appendRows(videos);
	};

	const refreshPagedStatus = (): void => {
		if (pager) {
			setMessage(statusEl, pager.options.formatStatus(rows.length, pager.nextOffset !== null));
		}
	};

	const loadNextPage = async (): Promise<void> => {
		const current = pager;
		if (!current || current.loading || current.nextOffset === null) {
			return;
		}

		const startedIn = generation;
		current.loading = true;
		renderSentinel(current.sentinel, { kind: 'loading' });

		let page: VideoPage;
		try {
			page = await current.options.loadMore(current.nextOffset);
		} catch (error) {
			if (startedIn === generation) {
				current.loading = false;
				renderSentinel(current.sentinel, {
					kind: 'error',
					message: describeError(error, 'Échec du chargement.'),
					onRetry: () => {
						loadNextPage().catch(logUnexpected);
					},
				});
			}
			return;
		}
		if (startedIn !== generation) {
			return;
		}

		appendRows(page.videos);
		current.nextOffset = page.nextOffset;
		current.loading = false;
		refreshPagedStatus();

		if (current.nextOffset === null) {
			renderSentinel(current.sentinel, { kind: 'end' });
			current.observer.disconnect();
			return;
		}
		renderSentinel(current.sentinel, { kind: 'idle' });
		// Si la fin de liste est encore visible (la page n'a pas suffi à remplir
		// la zone), l'observateur ne signalerait plus rien : on le réarme.
		current.observer.unobserve(current.sentinel);
		current.observer.observe(current.sentinel);
	};

	const showPaged = (options: ShowPagedOptions): void => {
		if (options.firstPage.nextOffset === null) {
			// Tout tient dans la première page : rien à paginer ni à libérer.
			showVideos(options.firstPage.videos, {
				status: options.formatStatus(options.firstPage.videos.length, false),
				allowAddAll: false,
			});
			return;
		}

		clear();
		header.hidden = false;
		addAllButton.hidden = true;

		const sentinel = document.createElement('li');
		sentinel.className = 'download-form__results-sentinel';
		list.append(sentinel);

		const observer = new IntersectionObserver(
			(observed) => {
				if (observed.some((entry) => entry.isIntersecting)) {
					loadNextPage().catch(logUnexpected);
				}
			},
			// Les résultats se chargent un peu avant d'atteindre la fin de liste.
			{ root: list, rootMargin: '0px 0px 300px 0px' },
		);

		pager = {
			options,
			sentinel,
			observer,
			nextOffset: options.firstPage.nextOffset,
			loading: false,
		};
		appendRows(options.firstPage.videos);
		refreshPagedStatus();
		observer.observe(sentinel);
	};

	return { showLoading, clear, showVideos, showPaged };
}

/**
 * Décrit une playlist chargée pour la ligne de statut de la liste.
 *
 * @param playlist - Playlist chargée.
 * @returns Un texte du type « Playlist « Titre » : 12 vidéo(s). ».
 */
function describePlaylist(playlist: PlaylistDto): string {
	const name = playlist.title ? `Playlist « ${playlist.title} »` : 'Playlist';
	return playlist.videos.length === 0
		? `${name} : aucune vidéo.`
		: `${name} : ${playlist.videos.length} vidéo(s).`;
}

/**
 * Décrit l'avancement d'une recherche pour la ligne de statut de la liste.
 *
 * @param count - Nombre de vidéos affichées.
 * @param hasMore - `true` s'il reste des résultats à charger en défilant.
 * @returns Un texte du type « 40 résultat(s) - d'autres se chargent en défilant. ».
 */
function describeSearchProgress(count: number, hasMore: boolean): string {
	if (count === 0) {
		return 'Aucun résultat.';
	}
	return hasMore
		? `${count} résultat(s) — d'autres se chargent en défilant.`
		: `${count} résultat(s).`;
}

/**
 * Charge une playlist et affiche ses vidéos dans la zone de résultats.
 *
 * @param url - Lien de la playlist.
 * @param results - Zone de résultats où afficher les vidéos.
 * @throws {Error} Si le chargement échoue (la zone de résultats est alors vidée).
 */
async function browsePlaylist(url: string, results: ResultsView): Promise<void> {
	results.showLoading('Chargement de la playlist...');

	let playlist: PlaylistDto;
	try {
		playlist = await fetchPlaylist(url);
	} catch (error) {
		results.clear();
		throw error;
	}

	results.showVideos(playlist.videos, {
		status: describePlaylist(playlist),
		allowAddAll: playlist.videos.length > 0,
	});
}

/**
 * Câble le formulaire d'ajout par URL : un lien de vidéo est ajouté
 * directement à la file, un lien de playlist affiche ses vidéos dans la zone
 * de résultats (à ajouter une par une ou d'un coup).
 *
 * @param root - Racine du formulaire monté.
 * @param results - Zone de résultats partagée.
 * @param callbacks - Rappels du formulaire.
 */
function wireUrlForm(
	root: HTMLElement,
	results: ResultsView,
	callbacks: DownloadFormCallbacks,
): void {
	const form = root.querySelector<HTMLFormElement>('form[data-form="url"]');
	const errorEl = root.querySelector<HTMLElement>('[data-role="url-error"]');
	if (!form) {
		return;
	}

	const handleSubmit = async (event: SubmitEvent): Promise<void> => {
		event.preventDefault();
		const url = form.querySelector<HTMLInputElement>('input[name="url"]')?.value.trim() ?? '';
		if (!url) {
			return;
		}

		setMessage(errorEl, null);
		const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
		if (submitButton) {
			submitButton.disabled = true;
		}

		try {
			if (isPlaylistUrl(url)) {
				await browsePlaylist(url, results);
			} else {
				await enqueueItem(url, readCurrentMode(root), null);
				callbacks.onItemQueued();
			}
			form.reset();
		} catch (error) {
			setMessage(errorEl, describeError(error, "Échec de l'ajout à la file."));
		} finally {
			if (submitButton) {
				submitButton.disabled = false;
			}
		}
	};

	form.addEventListener('submit', (event) => {
		handleSubmit(event).catch((error: unknown) => {
			console.error("Erreur inattendue lors de l'ajout par URL", error);
		});
	});
}

/**
 * Câble le formulaire de recherche YouTube globale. Avec une limite, les
 * résultats arrivent d'un coup ; sans limite, la liste se prolonge au fil du
 * défilement jusqu'à la fin des résultats de YouTube.
 *
 * @param root - Racine du formulaire monté.
 * @param results - Zone de résultats partagée.
 */
function wireSearchForm(root: HTMLElement, results: ResultsView): void {
	const form = root.querySelector<HTMLFormElement>('form[data-form="search"]');
	const errorEl = root.querySelector<HTMLElement>('[data-role="search-error"]');
	if (!form) {
		return;
	}

	// Recherche gardée ouverte côté serveur pour ses pages suivantes : libérée
	// quand la liste est remplacée (voir onClose), ou ici quand la page se
	// ferme ou se recharge - sans quoi son yt-dlp resterait suspendu jusqu'à
	// l'expiration côté serveur.
	let openSearchId: string | null = null;
	window.addEventListener('pagehide', () => {
		if (openSearchId) {
			closeSearch(openSearchId).catch(() => {
				// Meilleur effort : la page se ferme, personne pour lire une erreur.
			});
		}
	});

	const handleSubmit = async (event: SubmitEvent): Promise<void> => {
		event.preventDefault();
		const formData = new FormData(form);
		const query = String(formData.get('query') ?? '').trim();
		const limit = parseSearchLimit(String(formData.get('limit') ?? ''));
		if (!query) {
			return;
		}

		setMessage(errorEl, null);
		results.showLoading('Recherche en cours...');
		const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
		if (submitButton) {
			submitButton.disabled = true;
		}

		try {
			if (limit !== null) {
				// Limite fixée : tous les résultats en une fois, pas de défilement infini.
				const search = await startSearch(query, limit, limit);
				results.showVideos(search.videos, {
					status: describeSearchProgress(search.videos.length, false),
					allowAddAll: false,
				});
			} else {
				const search = await startSearch(query, null, SEARCH_PAGE_SIZE);
				if (search.nextOffset !== null) {
					// Sinon le serveur a déjà refermé la recherche : rien à libérer.
					openSearchId = search.id;
				}
				results.showPaged({
					firstPage: search,
					loadMore: (offset) => fetchSearchPage(search.id, offset, SEARCH_PAGE_SIZE),
					formatStatus: describeSearchProgress,
					onClose: () => {
						openSearchId = null;
						closeSearch(search.id).catch((error: unknown) => {
							console.warn('Échec de la fermeture de la recherche', error);
						});
					},
				});
			}
		} catch (error) {
			results.clear();
			setMessage(errorEl, describeError(error, 'Échec de la recherche.'));
		} finally {
			if (submitButton) {
				submitButton.disabled = false;
			}
		}
	};

	form.addEventListener('submit', (event) => {
		handleSubmit(event).catch((error: unknown) => {
			console.error('Erreur inattendue lors de la recherche YouTube', error);
		});
	});
}

/**
 * Câble le formulaire de recherche de chaîne.
 *
 * @param root - Racine du formulaire monté.
 * @param results - Zone de résultats partagée.
 */
function wireChannelForm(root: HTMLElement, results: ResultsView): void {
	const form = root.querySelector<HTMLFormElement>('form[data-form="channel"]');
	const errorEl = root.querySelector<HTMLElement>('[data-role="channel-error"]');
	if (!form) {
		return;
	}

	const handleSubmit = async (event: SubmitEvent): Promise<void> => {
		event.preventDefault();
		const formData = new FormData(form);
		const channel = String(formData.get('channel') ?? '').trim();
		const section = String(formData.get('section') ?? 'videos') as ChannelSection;
		const keywords = String(formData.get('keywords') ?? '');
		if (!channel) {
			return;
		}

		setMessage(errorEl, null);
		results.showLoading('Recherche en cours...');
		const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
		if (submitButton) {
			submitButton.disabled = true;
		}

		try {
			const videos = await searchChannel(channel, section, keywords);
			results.showVideos(videos, {
				status: videos.length === 0 ? 'Aucun résultat.' : `${videos.length} résultat(s).`,
				allowAddAll: false,
			});
		} catch (error) {
			results.clear();
			setMessage(errorEl, describeError(error, 'Échec de la recherche.'));
		} finally {
			if (submitButton) {
				submitButton.disabled = false;
			}
		}
	};

	form.addEventListener('submit', (event) => {
		handleSubmit(event).catch((error: unknown) => {
			console.error('Erreur inattendue lors de la recherche de chaîne', error);
		});
	});
}

/**
 * Monte le formulaire d'ajout (URL directe ou de playlist, recherche YouTube
 * globale, et recherche de chaîne) dans l'élément racine fourni.
 *
 * @param root - Élément DOM dans lequel injecter le formulaire.
 * @param callbacks - Rappels invoqués sur les actions du formulaire.
 */
export function mountDownloadForm(root: HTMLElement, callbacks: DownloadFormCallbacks): void {
	root.innerHTML = downloadFormTemplate;
	const results = createResultsView(root, callbacks);
	wireModeToggle(root);
	wireUrlForm(root, results, callbacks);
	wireSearchForm(root, results);
	wireChannelForm(root, results);
}
